export const META_TEMPLATE_COMPONENT_ORDER = Object.freeze({
  HEADER: 0,
  BODY: 1,
  FOOTER: 2,
  BUTTONS: 3
});

export const parseStoredJson = value => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch (_) {
    return null;
  }
};

const readStoredArray = value => {
  const parsed = parseStoredJson(value);
  if (Array.isArray(parsed)) return parsed;
  if (Array.isArray(parsed?.buttons)) return parsed.buttons;
  if (Array.isArray(parsed?.action?.buttons)) return parsed.action.buttons;
  return [];
};

export const normalizeStoredButtons = value =>
  readStoredArray(value)
    .filter(item => item && typeof item === "object")
    .map((item, index) => {
      const type = String(item.type || item.button_type || item.sub_type || "BUTTON").toUpperCase();
      const text = String(item.text || item.title || item.label || item.displayText || "").trim();
      if (!text) return null;
      return {
        key: String(item.key || item.id || item.index || `button_${index + 1}`),
        type,
        text,
        url: String(item.url || item.website || "").trim(),
        phoneNumber: String(item.phone_number || item.phoneNumber || "").trim()
      };
    })
    .filter(Boolean);

export const normalizeStoredExample = value => {
  const parsed = parseStoredJson(value);
  return parsed && typeof parsed === "object" ? parsed : null;
};

export const normalizeMetaTemplateComponents = template => {
  if (!Array.isArray(template?.components)) return [];

  return template.components
    .map((component, originalIndex) => ({
      component,
      originalIndex,
      type: String(component?.type || "").toUpperCase()
    }))
    .filter(item => Object.prototype.hasOwnProperty.call(META_TEMPLATE_COMPONENT_ORDER, item.type))
    .sort((left, right) => {
      const orderDifference = META_TEMPLATE_COMPONENT_ORDER[left.type] - META_TEMPLATE_COMPONENT_ORDER[right.type];
      return orderDifference || left.originalIndex - right.originalIndex;
    })
    .map(({ component, type, originalIndex }) => ({
      type,
      format: String(component?.format || "TEXT").toUpperCase(),
      text: String(component?.text || ""),
      example: normalizeStoredExample(component?.example),
      buttons: type === "BUTTONS" ? normalizeStoredButtons(component?.buttons) : [],
      originalIndex
    }));
};

const previewVariableValue = (componentType, variable, parameterConfig, contactName) => {
  const key = `${String(componentType || "").toLowerCase()}:${variable}`;
  const configured = parameterConfig?.[key] || (variable === "1" ? { source: "contact_name" } : null);
  const value = configured?.source === "contact_name"
    ? contactName
    : String(configured?.value || "").trim();

  if (!value) {
    return { value: `Preencher {{${variable}}}`, missing: variable };
  }
  return { value: String(value).slice(0, 1024), missing: null };
};

export const renderTemplatePreviewText = (
  text,
  componentType,
  parameterConfig,
  contactName = "Nome do contato"
) => {
  const missingVariables = [];
  const rendered = String(text || "").replace(/\{\{(\d+)\}\}/g, (_, variable) => {
    const resolved = previewVariableValue(componentType, variable, parameterConfig, contactName);
    if (resolved.missing && !missingVariables.includes(resolved.missing)) {
      missingVariables.push(resolved.missing);
    }
    return resolved.value;
  });
  return { text: rendered, missingVariables };
};

export const resolveHeaderMediaPreview = (format, parameterConfig) => {
  const normalizedFormat = String(format || "TEXT").toUpperCase();
  if (!["IMAGE", "VIDEO", "DOCUMENT"].includes(normalizedFormat)) return null;
  const value = String(parameterConfig?.["header:media"]?.value || "").trim();
  const valid = /^https:\/\//i.test(value);
  return {
    format: normalizedFormat,
    url: valid ? value : "",
    required: !valid
  };
};

export const buildOfficialTemplatePreviewModel = (
  template,
  parameterConfig,
  contactName = "Nome do contato"
) =>
  normalizeMetaTemplateComponents(template).map(component => ({
    ...component,
    // O envio atual aceita parametros apenas em HEADER e BODY. O FOOTER deve
    // permanecer literal para a previa nunca prometer uma substituicao que a
    // Meta nao recebera.
    rendered: ["HEADER", "BODY"].includes(component.type)
      ? renderTemplatePreviewText(component.text, component.type, parameterConfig, contactName)
      : { text: component.text, missingVariables: [] },
    media: component.type === "HEADER"
      ? resolveHeaderMediaPreview(component.format, parameterConfig)
      : null
  }));
